import { useContext, useState, useEffect } from "react";
import { DataProvider } from "../../store/ContextProvider";
import Loading from "../Loading";
import manBadge from "../../layouts/ui/MandatoryBadge";
import { IoIosEye, IoIosEyeOff } from "react-icons/io";
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import { changePassword } from "../../services/Authentication/changePassword";
import { passwordValidator, confirmPasswordValidator } from '../../utils/validator'

// ---------- Password strength helpers ----------
function StrengthBar({ score = 0 }) {
    const segments = [0, 1, 2, 3].map((i) => i < score);
    const colors = [
        "bg-red-400",
        "bg-orange-400",
        "bg-yellow-400",
        "bg-green-500",
    ];
    return (
        <div className="mt-2 flex gap-1 w-full max-w-xs">
            {segments.map((on, i) => (
                <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${on ? colors[i] : "bg-gray-200"
                        }`}
                />
            ))}
        </div>
    );
}

function evaluatePassword(password = "") {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
}

function smallStatus({ ok, text }) {
    return (
        <div
            className={`inline-flex items-center gap-1 text-xs ${ok ? "text-green-600" : "text-red-600"
                }`}
        >
            {ok ? <AiOutlineCheck /> : <AiOutlineClose />} <span>{text}</span>
        </div>
    );
}

// ---------- Main Component ----------
export default function ChangePassword() {
    const { BeURL, showAlert, isAuth, currentUser } = useContext(DataProvider);

    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [currentPasswordError, setCurrentPasswordError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);


    useEffect(() => {
        if (currentPassword !== "") {
            setCurrentPasswordError(passwordValidator(currentPassword))
        }
        else {
            setCurrentPasswordError("")
        }

        if (password !== "") {
            setPasswordError(passwordValidator(password))
        }
        else {
            setPasswordError("")
        }

        if (confirmPassword !== "") {
            setConfirmPasswordError(confirmPasswordValidator(confirmPassword, password))
        }
        else {
            setConfirmPasswordError("")
        }

    }, [currentPassword, password, confirmPassword])


    if (!currentUser || !isAuth) return <Loading />;

    // ---------- Validation ----------
    const validateAll = () => {
        let ok = true;

        if (!currentPassword) {
            setCurrentPasswordError("Current password is required.");
            ok = false;
        } else {
            setCurrentPasswordError("");
        }

        if (!password) {
            setPasswordError("New password is required.");
            ok = false;
        } else if (password.length < 8) {
            setPasswordError("Password must be at least 8 characters.");
            ok = false;
        } else if (password === currentPassword) {
            setPasswordError("New password cannot be the same as current password.");
            ok = false;
        } else {
            setPasswordError("");
        }

        if (!confirmPassword) {
            setConfirmPasswordError("Please confirm your new password.");
            ok = false;
        } else if (confirmPassword !== password) {
            setConfirmPasswordError("Passwords do not match.");
            ok = false;
        } else {
            setConfirmPasswordError("");
        }

        return ok;
    };

    const resetForm = () => {
        setCurrentPassword("");
        setPassword("");
        setConfirmPassword("");
        setShowCurrentPassword(false);
        setShowPassword(false);
        setShowConfirmPassword(false);
        setCurrentPasswordError("");
        setPasswordError("");
        setConfirmPasswordError("");
    };

    const handleUpdate = async () => {
        if (!validateAll()) {
            showAlert?.("Please fix errors before continuing.");
            return;
        }

        setSubmitting(true);
        try {
            await changePassword(BeURL, showAlert, currentPassword, password, resetForm);
        } catch (err) {
            console.error(err);
            showAlert?.("Something went wrong while updating password.");
        } finally {
            setSubmitting(false);
        }
    };

    const strength = evaluatePassword(password);
    const strengthLabel =
        strength === 0
            ? "Too weak"
            : strength === 1
                ? "Weak"
                : strength === 2
                    ? "Fair"
                    : strength === 3
                        ? "Good"
                        : "Strong";

    // ---------- UI ----------
    return (
        <div className="max-w-2xl mx-auto py-5 px-4 sm:px-0">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-md p-6 sm:p-8">
                <h3 className="text-2xl font-semibold text-gray-800">Change Password</h3>
                <p className="mt-1 text-sm text-gray-500">
                    Secure your account by updating your password.
                </p>

                <div className="mt-6 grid grid-cols-1 gap-5">
                    {/* Current Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Current Password {manBadge()}
                        </label>
                        <div className="mt-2 relative">
                            <input
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={(e) => {
                                    setCurrentPassword(e.target.value);
                                    setCurrentPasswordError("");
                                }}
                                placeholder="Enter current password"
                                className="w-full pr-10 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm shadow-sm focus:border-primary-300 focus:ring-1 focus:ring-primary-300 outline-none transition"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-gray-500"
                            >
                                {showCurrentPassword ? <IoIosEyeOff /> : <IoIosEye />}
                            </button>
                        </div>
                        {currentPasswordError && (
                            <p className="mt-1 text-sm text-red-600">{currentPasswordError}</p>
                        )}
                    </div>

                    {/* New Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            New Password {manBadge()}
                        </label>
                        <div className="mt-2 relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setPasswordError("");
                                }}
                                placeholder="Create a new password"
                                className="w-full pr-10 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm shadow-sm focus:border-primary-300 focus:ring-1 focus:ring-primary-300 outline-none transition"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-gray-500"
                            >
                                {showPassword ? <IoIosEyeOff /> : <IoIosEye />}
                            </button>
                        </div>

                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex flex-col gap-1">
                                <StrengthBar score={strength} />
                                <span className="text-xs text-gray-500 mt-1">{strengthLabel}</span>
                            </div>
                            <div className="mt-2 sm:mt-0">
                                {password && password.length >= 8
                                    ? smallStatus({ ok: true, text: "≥ 8 characters" })
                                    : smallStatus({ ok: false, text: "≥ 8 characters" })}
                            </div>
                        </div>

                        {passwordError && (
                            <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Confirm Password {manBadge()}
                        </label>
                        <div className="mt-2 relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setConfirmPasswordError("");
                                }}
                                placeholder="Re-enter new password"
                                className="w-full pr-10 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm shadow-sm focus:border-primary-300 focus:ring-1 focus:ring-primary-300 outline-none transition"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-gray-500"
                            >
                                {showConfirmPassword ? <IoIosEyeOff /> : <IoIosEye />}
                            </button>
                        </div>
                        {confirmPasswordError && (
                            <p className="mt-1 text-sm text-red-600">{confirmPasswordError}</p>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-3">
                        <div className="flex gap-2">
                            <button
                                onClick={handleUpdate}
                                disabled={submitting}
                                className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg shadow transition
                  ${submitting
                                        ? "bg-primary-100 cursor-not-allowed"
                                        : "bg-primary-300 hover:bg-primary-400"
                                    }`}
                            >
                                {submitting ? "Updating..." : "Update Password"}
                            </button>

                            <button
                                onClick={resetForm}
                                className="px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-secondary-300 hover:bg-gray-100 transition"
                            >
                                Reset
                            </button>
                        </div>

                        <p className="text-xs text-gray-500 sm:text-right">
                            Tip: Use a mix of letters, numbers, and symbols for better security.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
